import { Onboarding, OnboardingStep, Place } from "@xapp/components";
import React, { FC, useCallback, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

import { OnboardingProps } from "components/Onboarding/models";
import { useAnalyzeWebsiteMutation } from "generated/graphql";
import { wrapUrqlMutation } from "utils/graphql";

// Components

import { AddressStep, ConfirmProfileStep, WebsiteStep } from "../../components/V3";
import { usePlaceDetails } from "../../utils/usePlaceDetails";

import classes from "./style.scss";
import { ClaimAgentStep } from "components/Onboarding/components/V3/ClaimAgentStep";

function useQueryParams() {
    return new URLSearchParams(useLocation().search);
}

function useAddQueryParams() {
    const history = useHistory();

    type QueryParamsType = { [key: string]: string };
    return useCallback(
        (newParams: QueryParamsType) => {
            const searchParams = new URLSearchParams(history.location.search);
            for (const key in newParams) {
                const value = newParams[key];
                if (value && value !== "null") {
                    searchParams.set(key, newParams[key]);
                }
            }
            history.replace({ ...history.location, search: searchParams.toString() });
        },
        [history]
    );
}

export interface OnboardingV3StepContainerProps {
    onboarding: OnboardingProps;
    onNext(props: OnboardingProps): void;
    onAlternate?(props: OnboardingProps): void;
}

export const OnboardingV3Container: FC = () => {
    // TODO: Move to config variable
    const apiKey = "AIzaSyC5MBv-yVWy0YP4GpfsLd3JDPEBHoxzPCo";

    const setQueryParams = useAddQueryParams();
    const queryParams = useQueryParams();

    // const coupon = queryParams.get("coupon");
    // const product = queryParams.get("product");
    const step = queryParams.get("step") || "find_profile";
    //  const executionId = queryParams.get("executionId");
    const placeId = queryParams.get("placeId");

    const useWebsite = queryParams.get("useWebsite") === "true";

    const [place, loadingPlace] = usePlaceDetails(placeId);

    // These keep track of the props that will create the app as we move steps
    const [onboardingProps, setOnboardingProps] = useState<OnboardingProps>(() => {
        // in case the user refreshes, we store them (down below) on session storage
        // and try to retreive them
        let storageProps: OnboardingProps;
        try {
            storageProps = JSON.parse(sessionStorage.getItem("onboarding-appProps"));
        } catch (e) {
            // don't care about the error
            console.error(`Error parsing session storage onboarding-appProps: ${e}`);
        }
        return storageProps || {};
    });

    // create an effect that calls the google place details API with a placeId and returns the details

    useEffect(() => {
        if (place && onboardingProps?.place?.placeId !== place.placeId) {
            console.log("settng new place", place);
            setOnboardingProps((prev) => ({
                ...prev,
                place,
            }));
        }
    }, [onboardingProps?.place?.placeId, place]);

    const analyzeSite = wrapUrqlMutation(useAnalyzeWebsiteMutation(), {
        onSuccessMessage: false,
        onErrorMessage: true,
    });

    // They have selected a placeId and need to move on to confirm
    const handlePlaceId = useCallback(
        (place: Place) => {
            setQueryParams({
                step: "confirm_profile",
                placeId: place.placeId,
            });
        },
        [setQueryParams]
    );

    // They have confirmed the profile and need to move on to the next step
    const handleConfirmProfile = useCallback(
        (props: OnboardingProps) => {
            analyzeSite({ url: props.place.website }).then((result) => {
                setQueryParams({
                    placeId: props.place.placeId,
                    executionId: result.data.onboarding.startRetrieveWebsiteDetails.executionId,
                    step: "claim_agent",
                });
            });
        },
        [analyzeSite, setQueryParams]
    );

    // They selected they want to use the website instead of google business profile
    const handleUseWebsite = useCallback(() => {
        setQueryParams({
            useWebsite: "true",
            step: "use_website",
        });
    }, [setQueryParams]);

    const handleWebsite = useCallback(
        (url: string) => {
            analyzeSite({ url }).then((result) => {
                setQueryParams({
                    executionId: result.data.onboarding.startRetrieveWebsiteDetails.executionId,
                    step: "claim_agent",
                });
            });
        },
        [analyzeSite, setQueryParams]
    );

    const handleClaimAgent = useCallback(() => {
        // take them to login
    }, []);

    // BACK BUTTON

    const handleBack = useCallback(() => {
        let newStep;
        let newPlaceId = placeId;

        switch (step) {
            case "confirm_profile":
                newStep = "find_profile";
                break;
            case "use_website":
                newStep = "find_profile";
                newPlaceId = null;
                break;
            case "claim_agent":
                newStep = useWebsite ? "use_website" : "confirm_profile";
                break;
            default:
                newStep = "find_profile";
                break;
        }

        setQueryParams({
            step: newStep,
            placeId: newPlaceId,
        });
    }, [placeId, setQueryParams, step, useWebsite]);

    const headerOne = (
        <span>
            Train your <span style={{ color: "#1aa7dc" }}>AI Agent</span>{" "}
            <span style={{ color: "gray" }}>with your Google Business Profile</span>
        </span>
    );

    const headerOneWebsite = (
        <span>
            Train your <span style={{ color: "#1aa7dc" }}>AI Agent</span> with your website
        </span>
    );

    const headerTwo = (
        <span>
            {onboardingProps?.place?.name}'s <span style={{ color: "#1aa7dc" }}>AI Agent</span> Powers Google Book
            Online
        </span>
    );

    const asideOne = (
        <ul className={classes.asideList}>
            <li>
                <b>Find your profile </b> by entering your business name
            </li>
            <li>
                Your <span style={{ color: "#1aa7dc" }}>AI agent</span> will be trained on your profile
            </li>
            <li>Takes less than a minute!</li>
        </ul>
    );

    const asideTwo = (
        <ul className={classes.asideList}>
            <li>
                Your <span style={{ color: "#1aa7dc" }}>AI agent</span> will be on your data.
            </li>
            <li>
                Adds <span style={{ color: "#1aa7dc" }}>Book Online</span> to your Google Business Profile
            </li>
            <li>
                <b>Claim your AI Agent</b> to book jobs directly from your Google Business Profile and Google Maps
            </li>
        </ul>
    );

    return (
        <>
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100px" }}>
                <img
                    src={"/img/xapp-ai-horizontal-2color-rgb.png"}
                    alt="logo"
                    style={{ maxHeight: "50px", width: "auto", display: "block" }}
                />
            </div>
            <Onboarding onBack={handleBack}>
                <OnboardingStep
                    active={step === "find_profile"}
                    done={step !== "find_profile"}
                    hide={false}
                    header={headerOne}
                >
                    <AddressStep
                        aside={asideOne}
                        apiKey={apiKey}
                        onPlaceSelected={handlePlaceId}
                        onUseWebsite={handleUseWebsite}
                    />
                </OnboardingStep>
                <OnboardingStep
                    active={step === "confirm_profile"}
                    done={step === "claim_agent"}
                    hide={useWebsite}
                    header={headerOne}
                >
                    <ConfirmProfileStep
                        aside={asideOne}
                        loading={loadingPlace}
                        value={onboardingProps}
                        onConfirm={handleConfirmProfile}
                        onSearchAgain={() => {
                            setQueryParams({
                                placeId: null,
                                step: "find_profile",
                            });
                        }}
                    />
                </OnboardingStep>
                <OnboardingStep
                    active={step === "use_website"}
                    done={step === "claim_agent"}
                    hide={!useWebsite}
                    header={headerOneWebsite}
                >
                    <WebsiteStep onWebsite={handleWebsite} />
                </OnboardingStep>
                <OnboardingStep active={step === "claim_agent"} done={step === "claim_agent"} header={headerTwo}>
                    <ClaimAgentStep aside={asideTwo} value={onboardingProps} onClaim={handleClaimAgent} />
                </OnboardingStep>
            </Onboarding>
        </>
    );
};
